/**
 * Debate Rooms API client (M-X13).
 *
 * Talks to /api/cards/:cardId/debate-rooms (list/create/budget) and
 * /api/debate-rooms/:id/* (read/start/abort/import/stream). Live updates
 * arrive via SSE on /api/debate-rooms/:id/stream (handled by the
 * DebateTab component, not this client).
 *
 * Backend: apps/chaos-gateway/src/debate-rooms.ts
 */
import { apiJson } from './client';

export type DebateMode = 'round_robin' | 'parallel' | 'moderator';
export type DebateStopCriterion = 'max_rounds' | 'voting' | 'voting_diff' | 'manual';
export type DebateStatus =
  | 'pending'
  | 'running'
  | 'consensus'
  | 'aborted'
  | 'max_rounds_reached'
  | 'failed';

export interface DebateRoom {
  id: string;
  cardId: string;
  topic: string;
  mode: DebateMode;
  stopCriterion: DebateStopCriterion;
  maxRounds: number;
  currentRound: number;
  status: DebateStatus;
  finalConsensus: string | null;
  logPath: string | null;
  totalTokens: number;
  vendor: string;
  model: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface DebateParticipant {
  roleId: string;
  position: number;
  slug: string;
  displayName: string;
  icon: string | null;
  textColor: string;
}

export interface DebateMessage {
  id: string;
  roleId: string;
  roleSlug: string;
  roleDisplayName: string;
  roleTextColor: string;
  roundNumber: number;
  body: string;
  vote: 'agree' | 'disagree' | 'abstain' | null;
  voteReason: string | null;
  tokensUsed: number;
  durationMs: number;
  createdAt: string;
}

export interface DebateRoomDetail {
  room: DebateRoom;
  participants: DebateParticipant[];
  messages: DebateMessage[];
}

export interface CreateDebateRoomBody {
  topic: string;
  participantRoleIds: string[];
  mode: DebateMode;
  stopCriterion: DebateStopCriterion;
  maxRounds: number;
  vendor?: string;
  model?: string;
}

export interface BudgetEstimate {
  estimatedTokens: number;
  estimatedCostUsd: string;
  roles: number;
  maxRounds: number;
  mode: DebateMode;
}

export const debateRoomsApi = {
  list(cardId: string): Promise<DebateRoom[]> {
    return apiJson<{ items: DebateRoom[] }>(`/cards/${cardId}/debate-rooms`).then(
      (r) => r.items,
    );
  },
  create(cardId: string, body: CreateDebateRoomBody): Promise<DebateRoom> {
    return apiJson<{ room: DebateRoom }>(`/cards/${cardId}/debate-rooms`, {
      method: 'POST',
      json: body,
    }).then((r) => r.room);
  },
  budgetEstimate(
    cardId: string,
    params: { roles: number; maxRounds: number; mode: DebateMode },
  ): Promise<BudgetEstimate> {
    const qs = new URLSearchParams({
      roles: String(params.roles),
      maxRounds: String(params.maxRounds),
      mode: params.mode,
    }).toString();
    return apiJson<BudgetEstimate>(
      `/cards/${cardId}/debate-rooms/budget-estimate?${qs}`,
    );
  },
  get(id: string): Promise<DebateRoomDetail> {
    return apiJson<DebateRoomDetail>(`/debate-rooms/${id}`);
  },
  start(id: string): Promise<DebateRoom> {
    return apiJson<{ room: DebateRoom }>(`/debate-rooms/${id}/start`, {
      method: 'POST',
    }).then((r) => r.room);
  },
  abort(id: string): Promise<void> {
    return apiJson(`/debate-rooms/${id}/abort`, { method: 'POST' }).then(
      () => undefined,
    );
  },
  importToChat(id: string): Promise<{ sessionId: string; messageId: string | null }> {
    return apiJson<{ ok: true; sessionId: string; messageId: string | null }>(
      `/debate-rooms/${id}/import-to-chat`,
      { method: 'POST' },
    ).then((r) => ({ sessionId: r.sessionId, messageId: r.messageId }));
  },
};

export const debateRoomsKeys = {
  all: ['debate-rooms'] as const,
  list: (cardId: string) => ['debate-rooms', 'list', cardId] as const,
  detail: (id: string) => ['debate-rooms', 'detail', id] as const,
};
